import React, { useState, useEffect } from "react";
import {
    Modal,
    Tabs,
    Form,
    Input,
    Select,
    DatePicker,
    Upload,
    Button,
    Row,
    Col,
    message,
    Typography,
    Divider,
    Space,
    InputNumber
} from "antd";
import {
    UploadOutlined,
    SaveOutlined,
    ArrowLeftOutlined,
    DollarOutlined,
    CalendarOutlined,
    FileTextOutlined,
    InboxOutlined
} from "@ant-design/icons";
import dayjs from "dayjs";
import { Method_POST_CONTEXTO } from "../../../../../service/api";
import { NumericFormat } from "react-number-format";
import { useDraggableModal } from "../../Balancete/utils/modalDraggable";

const { Title, Text } = Typography;

const { TabPane } = Tabs;
const { Option } = Select;

export function ModalLancamentoEditar({ open, onClose, lancamento, zIndex, onFocus }) {
    const draggableProps = useDraggableModal(zIndex, onFocus, { open, modalKey: 'editBancarioIndiv' });
    const [form] = Form.useForm();
    const [fileList, setFileList] = useState([]);
    const [grupos, setGrupos] = useState([]);
    const [categorias, setCategorias] = useState([]);
    const [contas, setContas] = useState([]);
    const [loadingContas, setLoadingContas] = useState(false);
    const [loadingGrupos, setLoadingGrupos] = useState(false);
    const [loadingCategorias, setLoadingCategorias] = useState(false);

    const fetchGrupos = async (tipoId) => {
        try {
            setLoadingGrupos(true);
            const payload = { tipo: { id: tipoId } };
            const response = await Method_POST_CONTEXTO(
                "/contabil/lancamento/categoria/grupo/listar",
                payload
            );
            setGrupos(response || []);
        } catch (err) {
            console.error("Erro ao buscar grupos:", err);
            message.error("Não foi possível carregar grupos");
            setGrupos([]);
        } finally {
            setLoadingGrupos(false);
        }
    };

    const fetchCategorias = async (grupoId) => {
        try {
            setLoadingCategorias(true);
            const payload = { grupo: { id: grupoId }, situacao: { id: "A" } };
            const response = await Method_POST_CONTEXTO(
                "/contabil/lancamento/categoria/listar",
                payload
            );
            setCategorias(response || []);
        } catch (err) {
            console.error("Erro ao buscar categorias:", err);
            message.error("Não foi possível carregar categorias");
            setCategorias([]);
        } finally {
            setLoadingCategorias(false);
        }
    };

    const fetchContas = async () => {
        try {
            setLoadingContas(true);
            const response = await Method_POST_CONTEXTO(
                "/cliente/conta-bancaria/listar",
                {}
            );
            setContas(Array.isArray(response) ? response : []);
        } catch (err) {
            console.error("Erro ao buscar contas:", err);
            message.error("Não foi possível carregar contas bancárias");
            setContas([]);
        } finally {
            setLoadingContas(false);
        }
    };

    useEffect(() => {
        if (open && lancamento?.tipo?.id) {
            fetchGrupos(lancamento.tipo.id);
            fetchContas();
        }
    }, [open, lancamento]);

    useEffect(() => {
        if (open && lancamento) {
            form.setFieldsValue({
                grupo: lancamento.grupo?.id,
                categoria: lancamento.categoria?.id,
                contaBancaria: lancamento.contaBancaria?.id,
                dataReferencia: lancamento.dataReferencia
                    ? dayjs(lancamento.dataReferencia)
                    : null,
                valor: lancamento.valor ?? 0,
                situacao: lancamento.situacao?.id,
                descricao: lancamento.descricao,
            });

            if (lancamento.arquivos?.length) {
                setFileList(
                    lancamento.arquivos.map((file, idx) => ({
                        uid: idx,
                        name: file.nome,
                        status: "done",
                        url: file.url,
                    }))
                );
            }

            if (lancamento.grupo?.id) {
                fetchCategorias(lancamento.grupo.id);
            }
        }
    }, [open, lancamento, form]);

    const handleUploadChange = ({ fileList }) => setFileList(fileList);

    const handleSave = async () => {
        try {
            const values = await form.validateFields();

            const payload = {
                id: lancamento.id,
                cliente: lancamento.cliente,
                categoria: { id: values.categoria },
                tipo: lancamento.tipo,
                contaBancaria: { id: values.contaBancaria || "0" },
                dataReferencia: values.dataReferencia
                    ? values.dataReferencia.toISOString()
                    : null,
                descricao: values.descricao || "",
                situacao: { id: values.situacao },
                valor: values.valor
                    ? Number(String(values.valor).toString().replace(/\./g, "").replace(",", "."))
                    : 0, // ✅ envia como número
                // CRITICO: preserva o vinculo com a transacao Pluggy original.
                // Sem isso, o backend gravava NULL/vazio e a proxima sincronizacao
                // do Pluggy nao reconhecia o lancamento existente, criando duplicata.
                pluggyTransactionId: lancamento?.pluggyTransactionId ?? null,
            };

            await Method_POST_CONTEXTO("contabil/lancamento/atualizar", payload);

            message.success("Lançamento atualizado com sucesso!");
            onClose?.();
            form.resetFields();
            setFileList([]);
        } catch (err) {
            console.error("Erro ao atualizar lançamento:", err);
            message.error("Erro ao atualizar lançamento");
        }
    };

    const tipo = lancamento?.tipo?.id;
    const isCredit = tipo === 'C';
    const accentColor = isCredit ? '#059669' : '#e11d48';
    const bgColor = isCredit ? '#ecfdf5' : '#fff1f2';

    return (
        <Modal
            open={open}
            onCancel={onClose}
            footer={null}
            width={1000}
            className="premium-modal-v2"
            destroyOnClose
            {...draggableProps}
            closable={true}
            styles={{
                body: {
                    height: draggableProps.style?.height ? "calc(100% - 130px)" : "80vh",
                    padding: '16px 24px',
                    overflowY: "auto",
                    overflowX: "hidden"
                }
            }}
        >
            <div style={{ padding: '8px' }}>
                <header style={{
                    marginBottom: 32,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 16,
                    borderBottom: '1px solid #f1f5f9',
                    paddingBottom: 20
                }}>
                    <div style={{
                        width: 56,
                        height: 56,
                        borderRadius: 16,
                        backgroundColor: bgColor,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: accentColor,
                        fontSize: 24,
                        boxShadow: `0 4px 12px ${accentColor}20`
                    }}>
                        {isCredit ? <DollarOutlined /> : <FileTextOutlined />}
                    </div>
                    <div>
                        <Title level={4} style={{ margin: 0, color: '#0f172a' }}>
                            Alterar Lançamento
                        </Title>
                        <Text type="secondary">Confira e atualize as informações do lançamento financeiro.</Text>
                    </div>
                </header>

                <Form
                    form={form}
                    layout="vertical"
                    requiredMark={false}
                >
                    <Row gutter={[24, 0]}>
                        <Col xs={24} md={8}>
                            <Form.Item
                                name="grupo"
                                label={<Space><CalendarOutlined /> Grupo de Lançamento</Space>}
                                rules={[{ required: true, message: 'Obrigatório' }]}
                            >
                                <Select
                                    size="large"
                                    className="premium-select"
                                    placeholder="Selecione o grupo"
                                    onChange={(id) => fetchCategorias(id)}
                                    loading={loadingGrupos}
                                >
                                    {grupos.map((g) => (
                                        <Option key={g.id} value={g.id}>{g.nome}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item
                                name="categoria"
                                label={<Space><FileTextOutlined /> Categoria / Conta</Space>}
                                rules={[{ required: true, message: 'Obrigatório' }]}
                            >
                                <Select
                                    size="large"
                                    className="premium-select"
                                    placeholder="Selecione a categoria"
                                    loading={loadingCategorias}
                                >
                                    {categorias.map((c) => (
                                        <Option key={c.id} value={c.id}>{c.nome}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item
                                name="contaBancaria"
                                label={<Space><DollarOutlined /> Conta / Forma</Space>}
                                rules={[{ required: true, message: 'Obrigatório' }]}
                            >
                                <Select size="large" className="premium-select" placeholder="Selecione" loading={loadingContas}>
                                    <Option value="0">000 - Dinheiro (Caixa)</Option>
                                    {contas.map((c) => (
                                        <Option key={c.id} value={c.id}>
                                            {c.nome || c.banco?.nome} - Ag: {c.agencia} CC: {c.conta}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={[24, 0]}>
                        <Col xs={24} sm={12} md={8}>
                            <Form.Item
                                name="dataReferencia"
                                label="Data de Referência"
                                rules={[{ required: true, message: 'Obrigatório' }]}
                            >
                                <DatePicker
                                    style={{ width: '100%' }}
                                    format={["DD/MM/YYYY", "DDMMYYYY"]}
                                    size="large"
                                    className="premium-datepicker"
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={8}>
                            <Form.Item
                                name="valor"
                                label="Valor do Lançamento"
                                rules={[{ required: true, message: 'Obrigatório' }]}
                            >
                                <NumericFormat
                                    customInput={Input}
                                    thousandSeparator="."
                                    decimalSeparator=","
                                    prefix="R$ "
                                    style={{ width: '100%' }}
                                    className="premium-input-number"
                                    size="large"
                                    placeholder="0,00"
                                    decimalScale={2}
                                    fixedDecimalScale
                                    value={form.getFieldValue("valor")}
                                    onValueChange={(values) =>
                                        form.setFieldsValue({ valor: values.floatValue ?? 0 })
                                    }
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item
                                name="situacao"
                                label="Situação Atual"
                                rules={[{ required: true, message: 'Obrigatório' }]}
                            >
                                <Select size="large" className="premium-select">
                                    <Option value="A">Aberto</Option>
                                    <Option value="L">Liquidado</Option>
                                    <Option value="C">Cancelado</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item
                        name="descricao"
                        label="Descrição / Histórico"
                        rules={[{ required: true, message: 'Descreva o lançamento' }]}
                    >
                        <Input.TextArea rows={2} placeholder="Ex: Pagamento referente a nota fiscal #123..." className="premium-textarea" />
                    </Form.Item>

                    <div style={{ marginTop: 24, marginBottom: 24 }}>
                        <Divider orientation="left"><Text strong style={{ color: '#475569' }}>Documento e Comprovantes (Opcional)</Text></Divider>

                        <Row gutter={24}>
                            <Col xs={24}>
                                <Upload.Dragger
                                    maxCount={1}
                                    beforeUpload={() => false}
                                    fileList={fileList}
                                    onChange={handleUploadChange}
                                    className="premium-upload-dragger"
                                    disabled
                                >
                                    <p className="ant-upload-drag-icon">
                                        <InboxOutlined style={{ color: '#005A9E' }} />
                                    </p>
                                    <p className="ant-upload-text">Alteração de documento desabilitada na edição</p>
                                </Upload.Dragger>
                            </Col>
                        </Row>
                    </div>

                    <div
                        className="footer-buttons"
                        style={{
                            marginTop: 40,
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: 12,
                            paddingTop: 24,
                            borderTop: '1px solid #f1f5f9',
                            flexWrap: 'wrap'
                        }}
                    >
                        <Button
                            icon={<ArrowLeftOutlined />}
                            onClick={onClose}
                            size="large"
                            className="premium-btn-secondary"
                        >
                            Voltar
                        </Button>
                        <Button
                            type="primary"
                            icon={<SaveOutlined />}
                            onClick={handleSave}
                            size="large"
                            className="premium-btn-primary"
                        >
                            Salvar Alterações
                        </Button>
                    </div>
                </Form>
            </div>
        </Modal>
    );
}
